<template>
    <div class="modal-wrapper" @click="closeModal">
        <div class="modal-container" @click.stop>
            <div class="modal-btn-close" @click.stop="closeModal">
                <!-- close button using material icons -->
                <span class="material-symbols-outlined close-icon em2">cancel</span>
            </div>
            
            <div class="modal-body">
                <div class="modal-content-wrapper">
                    <div class="mini-deal-img-container">
                        <a :href="(settings.isShabbat ? '#' : dealUrl)" target="_blank" class="button-link" id="deal-url" @click="goToDeal($event)">
                            <div v-if="!isImageLoaded" class="blur-box shimmer"></div>
                            <img 
                                v-show="isImageLoaded"
                                :src="dealDetails.imgUrl" 
                                class="mini-deal-img"
                                :alt="dealDetails.title"
                                @load="onImageLoad"
                                @error="onImageError"
                            />
                            <!-- Always render image to trigger load event, but keep it hidden -->
                            <img 
                                v-if="!isImageLoaded"
                                :src="dealDetails.imgUrl"
                                alt=""
                                class="image-preloader"
                                @load="onImageLoad"
                                @error="onImageError"
                            />
                        </a>    
                    </div>
                    
                    <div class="modal-content-right">
                        <div class="mini-deal-body-content-title">
                            {{ dealDetails.title }}
                        </div>
                        <div class="mini-deal-prices">
                            <div :style="{'visibility': isDealNegativeOne ? 'hidden' : 'visible'}">
                                <div class="crossed-price"><span v-if='dealDetails.retailPrice'>{{formatPrice(dealDetails.retailPrice)}}</span></div>
                                <div class="deal-price-container" >
                                    <div class="deal-price">{{formatPrice(dealDetails.dealPrice)}}</div>
                                    <div class="deal-discount" v-if="discount">-{{discount.toFixed(0)}}%</div>
                                </div>
                            </div>
                        </div>
                        <div class="last-updated-section" v-if="dealDetails.updateDate">
                            <span class="last-updated-label">Last updated:</span>
                            <span class="last-updated-date">{{formatLastUpdatedDate(dealDetails.updateDate)}}</span>
                        </div>
                        <div class="deal-provider">
                            <DealLogoImage :deal="dealDetails" class="logo-img" />
                        </div>
                        <div class="deal-promo-code" v-if="dealDetails.promoCode">
                            <div class="deal-promo-code-title">Promo Code</div>
                            <div class="share-link-container">
                                <input id="promoCode" v-model="dealDetails.promoCode" class="promo-code-input" readonly />
                                <a v-show="!copied" class="promo-copy-code" @click="copyToClipboard">COPY</a>
                                <a v-show="copied" class="promo-copy-code copied">COPIED</a>
                            </div>
                        </div>
                        
                        <div class="mini-deal-buttons">
                            <div class="deal-buttons-row-1">
                                <a :href="(settings.isShabbat ? '#' : dealUrl)" target="_blank" class="button-link" id="deal-url" @click="goToDeal($event)">
                                    <button class="primary-button centered">
                                        Get Deal
                                    </button>
                                </a>
                            </div>
                            <div class="deal-buttons-row-2">
                                <ShareButton :link="dealHelper.getDealLink(dealDetails)" :messageTitle="'Check out this deal!'" :messageText="'Found this amazing deal on MommyDeals - thought you would like it.'" />
                                <GetDealAlerts />
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="mini-deal-disclaimer">
                    Disclaimer: We may receive compensation from brands for some of the deals we feature, including promoted items.
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { DealDetails } from '@/classes/DealDetails'
import { globalStore } from '@/stores/globalStore'
import ga from '@/helpers/ga'
import clicky from '@/helpers/clicky'
import settings from '@/settings'
import util from '@/helpers/util'
import dealHelper from '@/helpers/deal-helper'
import ModalDealAlerts from '@/modals/ModalDealAlerts.vue'
import DealLogoImage from './DealLogoImage.vue'
import ShareButton from './ShareButton.vue'
import GetDealAlerts from './GetDealAlerts.vue'

const $globalStore = globalStore()
const route = useRoute()

const props = defineProps<{
    dealDetails: DealDetails
    showDealPrice: boolean
}>()

const showModalDealAlerts = ref(false)
const isImageLoaded = ref(false)
let savedScrollPosition = 0
let isLocked = false

// Lock body scroll when modal is open
const lockBodyScroll = () => {
    // Don't lock if already locked
    if (isLocked) return
    
    // First, try to get preserved scroll position from DealBox (set before modal opens)
    const preservedScroll = document.body.getAttribute('data-preserved-scroll')
    if (preservedScroll) {
        savedScrollPosition = parseInt(preservedScroll, 10)
        document.body.removeAttribute('data-preserved-scroll')
    } else {
        // Fallback: Get scroll position from multiple sources to ensure accuracy
        savedScrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
    }
    
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollPosition}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
    isLocked = true
}

// Unlock body scroll when modal is closed
const unlockBodyScroll = () => {
    // Don't unlock if not locked
    if (!isLocked) return
    
    // Store the scroll position before unlocking - this is critical
    const scrollToRestore = savedScrollPosition
    
    // Mark as unlocked first to prevent re-locking
    isLocked = false
    
    // Get the current top value (negative scroll position)
    const currentTop = document.body.style.top
    
    // Reset body styles
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    
    // Restore scroll position immediately after removing fixed positioning
    // Use both window.scrollTo and document.documentElement.scrollTop for compatibility
    if (scrollToRestore > 0) {
        // First attempt - immediate
        window.scrollTo(0, scrollToRestore)
        document.documentElement.scrollTop = scrollToRestore
        document.body.scrollTop = scrollToRestore
        
        // Second attempt - after layout recalculation
        requestAnimationFrame(() => {
            window.scrollTo(0, scrollToRestore)
            document.documentElement.scrollTop = scrollToRestore
            
            // Final check after a brief delay
            setTimeout(() => {
                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0
                if (Math.abs(currentScroll - scrollToRestore) > 1) {
                    window.scrollTo(0, scrollToRestore)
                    document.documentElement.scrollTop = scrollToRestore
                }
            }, 10)
        })
    }
    
    // Reset saved position after restoration
    setTimeout(() => {
        savedScrollPosition = 0
    }, 100)
}

// Watch global store for modal state changes
watch(() => $globalStore.selectedDeal, (newVal) => {
    if (newVal) {
        // Capture scroll position synchronously before locking
        // This must happen immediately, not in a frame callback
        lockBodyScroll()
    } else {
        unlockBodyScroll()
    }
})

onMounted(() => {
    // Only lock if modal is already open when component mounts
    if ($globalStore.selectedDeal) {
        lockBodyScroll()
    }
})

onBeforeUnmount(() => {
    unlockBodyScroll()
})

const isDealNegativeOne = computed(() => {
    return props.dealDetails.dealPrice < 0 || !props.showDealPrice
})

const discount = computed(() => {
    return util.getDiscountPercentage(props.dealDetails.dealPrice, props.dealDetails.retailPrice)
})

// Generate deal URL - check if it's a gift list with searchQuery
const dealUrl = computed(() => {
    const deal = props.dealDetails
    
    // Check if it's a gift list page
    const isGiftListPage = route.name === 'giftideas' || route.name === 'giftlist' || route.name === 'giftlist2'
    
    // Get affiliate info for this site
    const affiliateInfo = util.getAffiliateParams(settings.siteName)
    
    if (isGiftListPage && deal.searchQuery && deal.searchQuery.trim() !== '') {
        // Generate Amazon search URL with affiliate tag
        const encodedQuery = encodeURIComponent(deal.searchQuery.trim())
        return `https://www.amazon.com/s?k=${encodedQuery}&tag=${affiliateInfo.amazonTag}`
    }
    
    // Always ensure affiliate tag is present on deal URL
    let url = deal.dealUrl || deal.dealURL || ''
    if (url && affiliateInfo.amazonTag) {
        url = util.replaceOrAddUrlParam(url, 'tag', affiliateInfo.amazonTag)
    }
    return url
})

const closeModal = (event?: Event) => {
    if (event) {
        event.preventDefault()
        event.stopPropagation()
    }
    unlockBodyScroll()
    $globalStore.setSelectedDeal(null)
}

const formatPrice = (price: number) => {
    return price.toFixed(2)
}

const formatLastUpdatedDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    
    // Format as "Sep 23, 2025 • 10:42 AM"
    const dateStr = dateObj.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
    });
    
    const timeStr = dateObj.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
    
    return `${dateStr} • ${timeStr}`;
}

const copied = ref(false)

const copyToClipboard = () => {
    navigator.clipboard.writeText(props.dealDetails.promoCode)
    copied.value = true
    setTimeout(() => {
        copied.value = false
    }, 2000)
}

const trackOutboundLink = (url: string) => {
            ga.event('click', { 'event_category': 'outbound', 'event_label': url, 'transport_type': 'beacon' })
            clicky.goal('DealClick2')
        }

const goToDeal = (event?: Event) => {
            if (settings.isShabbat) {
                if (event) {
                    event.preventDefault()
                    event.stopPropagation()
                }
                alert(`Thank you for your patience while ${settings.siteNameCaps} breaks for the Shabbos holiday.

Deal links will resume to work on ${settings.shabbatEnds}.`)
            }
            else {
                // Track the outbound link - navigation happens naturally via anchor href
                trackOutboundLink(dealUrl.value)
                // Don't prevent default - let the anchor navigate naturally to avoid popup blockers
            }
}

const onImageLoad = () => {
    isImageLoaded.value = true
}

const onImageError = () => {
    // Keep shimmer visible if image fails to load
    isImageLoaded.value = false
}

</script>

<style scoped>
/* Mobile-first base styles */
.mini-deal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.mini-deal-header-title {
    font-size: 1em;
}

.modal-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow-y: auto;
    animation: fadeIn 0.3s ease-out;
}

.modal-container {
    background-color: #fff;
    width: 90%;
    max-width: 500px;
    border-radius: 8px;
    padding: 20px;
    position: relative;
    margin: 20px;
    max-height: 90vh;
    overflow-y: auto;
    z-index: 10000;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    animation: popIn 0.3s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes popIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
}

.modal-btn-close {
    position: absolute;
    right: 15px;
    top: 15px;
    cursor: pointer;
    z-index: 10001;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    pointer-events: auto;
    transition: all 0.2s ease;
}

.modal-btn-close:hover {
    background-color: rgba(255, 255, 255, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transform: scale(1.1);
}

.modal-btn-close .close-icon {
    pointer-events: none;
    user-select: none;
}

.modal-body {
    display: flex;
    flex-direction: column;
}

.modal-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.modal-content-right {
    display: flex;
    flex-direction: column;
}

.mini-deal-img-container {
    position: relative;
    min-height: 200px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    padding-top: 10px;
}

img.mini-deal-img {
    max-height: 200px;
    max-width: 100%;
    height: auto;
    width: auto;
}

.blur-box {
    position: absolute;
    width: 75%;
    max-width: 75%;
    height: 200px;
    max-height: 200px;
}

.shimmer {
    animation-duration: 1s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: placeHolderShimmer;
    animation-timing-function: linear;
    background: #f6f7f9;
    background-image: linear-gradient(to right, #f6f7f9 0%, #e9ebee 20%, #f6f7f9 40%, #f6f7f9 100%);
    background-repeat: no-repeat;
    background-size: 800px 104px;
    border-radius: 4px;
}

@keyframes placeHolderShimmer {
    0% {
        background-position: -468px 0;
    }
    100% {
        background-position: 468px 0;
    }
}

.image-preloader {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.mini-deal-body-content-title {
    font-size: 1.1em;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 10px;
    color: #333;
}

.deal-price {
    font-size: 1.5em;
    font-weight: bold;
}

.last-updated-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-top: 3px;
    margin-bottom: 3px;
    font-size: 0.85em;
    color: #666;
}

.last-updated-label {
    font-weight: 500;
}

.last-updated-date {
    color: #888;
}

.deal-provider {
    margin-top: 10px;
    margin-bottom: 10px;
}

.mini-deal-buttons {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 100%;
    margin-top: 15px;
}

.deal-buttons-row-1 {
    width: 100%;
}

.deal-buttons-row-1 .button-link {
    width: 100%;
}

.deal-buttons-row-2 {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
}

.deal-buttons-row-2 > * {
    width: 100%;
}

.mini-deal-disclaimer {
    font-size: 0.75em;
    color: #666;
    text-align: center;
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}

/* Desktop styles (768px and up) */
@media all and (min-width: 768px) {
    .modal-container {
        max-width: 900px;
        padding: 40px;
        border-radius: 12px;
    }

    .modal-btn-close {
        right: 25px;
        top: 25px;
        width: 40px;
        height: 40px;
    }

    .modal-content-wrapper {
        flex-direction: row;
        gap: 40px;
        align-items: flex-start;
    }

    .mini-deal-img-container {
        flex: 0 0 40%;
        min-height: 350px;
        padding-top: 0;
        align-items: center;
    }

    img.mini-deal-img {
        max-height: 400px;
        max-width: 100%;
    }

    .blur-box {
        width: 100%;
        max-width: 100%;
        height: 350px;
        max-height: 350px;
    }

    .modal-content-right {
        flex: 1;
        min-width: 0;
    }

    .mini-deal-body-content-title {
        font-size: 1.5em;
        margin-bottom: 15px;
    }

    .deal-price {
        font-size: 2em;
    }

    .mini-deal-prices {
        margin-top: 15px;
        margin-bottom: 20px;
    }

    .last-updated-section {
        margin-top: 5px;
        margin-bottom: 5px;
        font-size: 0.9em;
    }

    .deal-provider {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    .mini-deal-buttons {
        gap: 15px;
        margin-top: 20px;
    }

    .deal-buttons-row-1 {
        width: 100%;
    }

    .deal-buttons-row-2 {
        flex-direction: row;
        gap: 12px;
        width: 100%;
    }

    .deal-buttons-row-2 > * {
        flex: 1;
        min-width: 150px;
        width: auto;
    }

    .mini-deal-disclaimer {
        font-size: 0.85em;
        margin-top: 20px;
        padding-top: 20px;
    }
}

/* Tablet and larger desktop (1024px and up) */
@media all and (min-width: 1024px) {
    .modal-container {
        max-width: 1000px;
        padding: 50px;
    }

    .modal-content-wrapper {
        gap: 50px;
    }

    .mini-deal-img-container {
        flex: 0 0 45%;
        min-height: 400px;
    }

    img.mini-deal-img {
        max-height: 450px;
    }

    .blur-box {
        height: 400px;
        max-height: 400px;
    }

    .mini-deal-body-content-title {
        font-size: 1.75em;
    }

    .deal-price {
        font-size: 2.25em;
    }
}

/* Mobile-specific adjustments */
@media all and (max-width: 767px) {
    .mini-deal-img-container {
        min-height: auto;
        padding-top: 5px;
        padding-bottom: 5px;
    }

    img.mini-deal-img {
        max-height: 250px;
    }

    .blur-box {
        height: 200px;
        max-height: 200px;
    }
}

</style>
